import { z } from 'zod';
import type { CommissioningThreshold, CommissioningThresholdBandStructure } from '../types/commissioning';

/** Reference inventory only: shared by validation/snapshot/configuration.
 * This does not evaluate a condition or an economic amount. Accepts both the
 * template structure and the composed effective bands (same function slots). */
export function collectThresholdEconomicFunctionIds(
  bands: readonly (CommissioningThreshold | CommissioningThresholdBandStructure)[],
): Set<string> {
  return new Set(bands.flatMap((band) => [
    ...(band.admission ? [band.admission.functionId] : []),
    ...(band.categories ?? []).map((rule) => rule.functionId),
    ...(band.surcharges ?? []).map((rule) => rule.functionId),
  ]));
}

export const commissioningThresholdValiditySchema = z.object({
  status: z.enum(['valid', 'not_evaluable']),
  reason: z.string().nullable(),
  code: z.enum([
    'incomplete_history', 'missing_measure', 'invalid_denominator', 'invalid_target',
    'source_missing', 'source_pending', 'source_stale', 'source_scope_invalid', 'missing_payment_unit',
    'condition_not_evaluable', 'incompatible_indicator', 'values_incomplete',
  ]).optional(),
}).strict();
export type CommissioningThresholdValidity = z.infer<typeof commissioningThresholdValiditySchema>;

export const commissioningThresholdAdmissionDecisionSchema = z.object({
  naturalBandId: z.string().uuid().nullable(),
  effectiveBandId: z.string().uuid().nullable(),
  naturalIndex: z.number().int().nonnegative(),
  effectiveIndex: z.number().int().nonnegative(),
  validity: commissioningThresholdValiditySchema,
  allowedFlatBandIds: z.array(z.string()),
  conditions: z.array(z.object({
    bandId: z.string().uuid().nullable(),
    functionId: z.string().uuid(),
    outcome: z.enum(['match', 'no_match', 'not_evaluable']),
  }).strict()),
}).strict();
export type CommissioningThresholdAdmissionDecision = z.infer<typeof commissioningThresholdAdmissionDecisionSchema>;

/** Parametri economici valorizzabili da categorie e maggiorazioni (fonti non flat). */
export const COMMISSIONING_THRESHOLD_PARAMETER_KEYS = ['canoneMultiplier', 'gettoneGaraMultiplier', 'perUnitEur', 'percentOfAmount'] as const;
export const commissioningThresholdParameterKeySchema = z.enum(COMMISSIONING_THRESHOLD_PARAMETER_KEYS);
export type CommissioningThresholdParameterKey = z.infer<typeof commissioningThresholdParameterKeySchema>;

/** Category cells inherit only when omitted. Zero is an explicit override. */
export const commissioningThresholdParametersSchema = z.object({
  canoneMultiplier: z.number().finite().nonnegative().optional(),
  gettoneGaraMultiplier: z.number().finite().nonnegative().optional(),
  perUnitEur: z.number().finite().nonnegative().optional(),
  /** % (0–100) sull'importo netto dell'evento: categorie sostituiscono, maggiorazioni sommano (tetto 100 al calcolo). */
  percentOfAmount: z.number().finite().min(0).max(100).optional(),
}).strict();

const parameterKeyList = z.array(commissioningThresholdParameterKeySchema).max(COMMISSIONING_THRESHOLD_PARAMETER_KEYS.length)
  .refine((keys) => new Set(keys).size === keys.length, 'Parametro ripetuto');

/**
 * STRUTTURA di una categoria nel template: nome, condizione, priorità e
 * l'elenco dei parametri che verranno valorizzati per cluster (nessun numero).
 */
export const commissioningThresholdCategoryStructureSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  priority: z.number().int().nonnegative(),
  functionId: z.string().uuid(),
  parameters: parameterKeyList,
}).strict();
export type CommissioningThresholdCategoryStructure = z.infer<typeof commissioningThresholdCategoryStructureSchema>;

/** STRUTTURA di una maggiorazione: nome, condizione e parametri del delta (≥ 1). */
export const commissioningThresholdSurchargeStructureSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  functionId: z.string().uuid(),
  delta: parameterKeyList.refine((keys) => keys.length > 0, 'Indica almeno un parametro da maggiorare'),
}).strict();
export type CommissioningThresholdSurchargeStructure = z.infer<typeof commissioningThresholdSurchargeStructureSchema>;

/** Categoria EFFETTIVA (struttura + valori composti): consumata dal motore. */
export const commissioningThresholdCategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  priority: z.number().int().nonnegative(),
  functionId: z.string().uuid(),
  parameters: commissioningThresholdParametersSchema,
}).strict();

/** Maggiorazione EFFETTIVA (struttura + valori composti): consumata dal motore. */
export const commissioningThresholdSurchargeSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  functionId: z.string().uuid(),
  delta: commissioningThresholdParametersSchema.refine(
    (value) => Object.keys(value).length > 0,
    'Indica almeno una maggiorazione (non negativa)',
  ),
}).strict();

/**
 * Parametri EFFETTIVI della traccia economica (base/categoria + somma delle
 * maggiorazioni). La % sull'importo può superare 100 dopo le maggiorazioni:
 * il motore la segnala come non valutabile, ma la traccia deve poterla
 * rappresentare. Il tetto 0–100 resta sui valori INSERITI (banda/categoria).
 */
export const commissioningThresholdEffectiveParametersSchema = commissioningThresholdParametersSchema.extend({
  percentOfAmount: z.number().finite().min(0).optional(),
}).strict();
export type CommissioningThresholdEffectiveParameters = z.infer<typeof commissioningThresholdEffectiveParametersSchema>;

export const commissioningThresholdEconomicsTraceSchema = z.object({
  status: z.enum(['valid', 'not_evaluable']),
  reason: z.string().nullable(),
  code: commissioningThresholdValiditySchema.shape.code,
  categoryId: z.string().uuid().nullable(),
  surchargeIds: z.array(z.string().uuid()),
  parameters: commissioningThresholdEffectiveParametersSchema,
  conditions: z.array(z.object({
    kind: z.enum(['category', 'surcharge']),
    ruleId: z.string().uuid(),
    functionId: z.string().uuid(),
    outcome: z.enum(['match', 'no_match', 'not_evaluable']),
  }).strict()),
}).strict();

/**
 * Scala applicata ai limiti di banda del partecipante: fattore e limiti
 * dichiarati/effettivi della banda applicata (traccia di spiegazione, non
 * una seconda fonte di verità sulla banda).
 */
export const commissioningThresholdScaleTraceSchema = z.object({
  kind: z.enum(['absolute', 'per_store']),
  /** PdV congelati nel contesto comune per il soggetto (null = fattore 1 non derivato da PdV). */
  storeCount: z.number().int().nonnegative().nullable(),
  factor: z.number().finite().positive(),
  /** Limiti dichiarati della banda applicata, prima del fattore (null = nessuna banda). */
  declaredFrom: z.number().finite().nullable(),
  declaredTo: z.number().finite().nullable(),
  /** Limiti effettivi (dichiarati × fattore) della banda applicata. */
  effectiveFrom: z.number().finite().nullable(),
  effectiveTo: z.number().finite().nullable(),
}).strict();
export type CommissioningThresholdScaleTrace = z.infer<typeof commissioningThresholdScaleTraceSchema>;

export type CommissioningThresholdParameters = z.infer<typeof commissioningThresholdParametersSchema>;
export type CommissioningThresholdCategory = z.infer<typeof commissioningThresholdCategorySchema>;
export type CommissioningThresholdSurcharge = z.infer<typeof commissioningThresholdSurchargeSchema>;
export type CommissioningThresholdEconomicsTrace = z.infer<typeof commissioningThresholdEconomicsTraceSchema>;
